import { GetTenantsRequestConfig, SwitchProjectRequestConfig, useStorytellClient } from "@storytell/client";
import { subscribeToGlobalEvent, subscribeToServerEvent } from "@storytell/events";
import type { ProjectId, TenantSummary } from "@storytell/types";
import { createResource, onCleanup, onMount } from "solid-js";
import { isServer } from "solid-js/web";

export type TenantsStore = ReturnType<typeof createTenantsStore>;

export const createTenantsStore = (deps: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  userId: () => string | undefined;
}) => {
  const client = useStorytellClient();
  // Preserve last-good so a transient failure doesn't blank the org switcher.
  // Tagged by userId so a same-tab re-auth can't leak the prior user's tenants.
  let lastSuccessful: { userId: string; value: TenantSummary[] } | undefined;

  const [tenantsData, { refetch }] = createResource(
    () => deps.authReady() && deps.userId(),
    async (): Promise<TenantSummary[]> => {
      const currentUserId = deps.userId();
      if (lastSuccessful && lastSuccessful.userId !== currentUserId) {
        lastSuccessful = undefined;
      }
      const request = GetTenantsRequestConfig.withBearerToken(deps.authToken());
      const response = await client.fetch(request, undefined, undefined);
      if (response.isOk()) {
        if (currentUserId) lastSuccessful = { userId: currentUserId, value: response.value.tenants };
        return response.value.tenants;
      }
      const cached = lastSuccessful;
      return cached && cached.userId === currentUserId ? cached.value : [];
    },
    { deferStream: true, initialValue: [] }
  );

  if (!isServer) {
    onMount(() => {
      const unsubscribeConnectivityRestored = subscribeToGlobalEvent("connectivity-restored", () => {
        void refetch();
      });

      const unsubscribeAuthRefresh = subscribeToGlobalEvent("auth-token-refreshed", () => {
        void refetch();
      });

      // Only create/delete can introduce or revoke a tenant/org/workspace in
      // the user's accessible set; role updates inside an already-visible org
      // don't change the org switcher's contents.
      const unsubscribeServerEvent = subscribeToServerEvent("cp.grants", (serverEvent) => {
        if (serverEvent.op !== "op.create" && serverEvent.op !== "op.delete") return;
        void refetch();
      });

      onCleanup(() => {
        unsubscribeConnectivityRestored();
        unsubscribeAuthRefresh();
        unsubscribeServerEvent();
      });
    });
  }

  const tenants = () => tenantsData.latest ?? [];

  // Fire-and-forget: persists the landing-project preference for next session.
  const persistSelection = (projectId: ProjectId): Promise<void> => {
    const request = SwitchProjectRequestConfig.withBearerToken(deps.authToken());
    return client
      .fetch(request, undefined, { projectId })
      .then(() => undefined)
      .catch(() => undefined);
  };

  return {
    refetch,
    tenants,
    persistSelection,
  };
};
